import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Button, Chip, Stack } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-premium';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useRef, useState } from 'react';
import { AsyncDialog, AsyncDialogRef } from '../../../../components/AsyncDialog/AsyncDialog';
import {
  ConfirmationDialog,
  ConfirmationDialogData,
} from '../../../../components/ConfirmationDialog/ConfirmationDialog';
import { TableCard } from '../../../../components/Section';
import { RtkQueryTable, RtkQueryTableModels } from '../../../../components/Table/RtkQueryTable';
import {
  TREASURY_FIRST_PAGE,
  TREASURY_GRID_HEIGHT,
  TREASURY_LIST_PAGE_SIZE,
  TREASURY_WITHDRAW_PENDING,
  TREASURY_WITHDRAW_STATUS_COLORS,
} from '../../../../constants/treasury';
import { usePermissions } from '../../../../hooks';
import {
  useApproveTreasuryWithdrawRequestMutation,
  useCreateTreasuryWithdrawRequestMutation,
  useFetchTreasuryWithdrawRequestsQuery,
  useRejectTreasuryWithdrawRequestMutation,
} from '../../../../redux/features/treasury/treasury.apiSlice';
import { CompanyWithdrawRequestRaw, CreateCompanyWithdrawRequestRequest } from '../../../../types/b2tRatibiPlus';
import * as permissions from '../../../../utils/permissions';
import { getTreasuryErrorMessage } from '../../../../utils/treasury';
import { TreasuryWithdrawDialog } from './TreasuryWithdrawDialog';

type TreasuryWithdrawalsTabProps = {
  companyId: number;
};

/**
 * Withdrawals — cash out of the company treasury bank to the company's own account. Creating holds the amount;
 * approve settles the hold, reject releases it. Only a pending request can be approved or rejected.
 */
export const TreasuryWithdrawalsTab = ({ companyId }: TreasuryWithdrawalsTabProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const canCreate = usePermissions({ items: [permissions.Treasury.createWithdrawal] });
  const canUpdate = usePermissions({ items: [permissions.Treasury.updateWithdrawal] });
  const confirmRef = useRef<AsyncDialogRef<ConfirmationDialogData, boolean>>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [tableModels, setTableModels] = useState<RtkQueryTableModels>({ paginationModel: TREASURY_FIRST_PAGE });
  // The grid keeps its page itself; a new key opens it again on the first page.
  const [tableKey, setTableKey] = useState(0);
  const { page } = tableModels.paginationModel;
  // Rows are cached per page, so the grid only ever shows the page it is on.
  const { data, isFetching, isError, error, refetch } = useFetchTreasuryWithdrawRequestsQuery({
    companyId,
    page: page + 1,
    limit: TREASURY_LIST_PAGE_SIZE,
  });
  const [createWithdrawRequest, { isLoading: isSaving }] = useCreateTreasuryWithdrawRequestMutation();
  const [approveWithdrawRequest] = useApproveTreasuryWithdrawRequestMutation();
  const [rejectWithdrawRequest] = useRejectTreasuryWithdrawRequestMutation();

  useEffect(() => {
    if (isError) {
      enqueueSnackbar(getTreasuryErrorMessage(error, 'Failed to load the withdraw requests'), { variant: 'error' });
    }
  }, [isError, error, enqueueSnackbar]);

  const create = async (payload: CreateCompanyWithdrawRequestRequest) => {
    try {
      await createWithdrawRequest({ companyId, payload }).unwrap();
      enqueueSnackbar('Withdraw request created — the amount is on hold', { variant: 'success' });
      setIsDialogOpen(false);

      // The new row is on the first page. On it already, the create refetched it.
      if (page !== 0) {
        setTableModels({ paginationModel: TREASURY_FIRST_PAGE });
        setTableKey((key) => key + 1);
      }
    } catch (createError) {
      enqueueSnackbar(getTreasuryErrorMessage(createError, 'Failed to create the withdraw request'), {
        variant: 'error',
      });
    }
  };

  const columns = useMemo<GridColDef<CompanyWithdrawRequestRaw>[]>(() => {
    const decide = (request: CompanyWithdrawRequestRaw, approve: boolean) =>
      confirmRef.current?.show({
        title: approve ? 'Approve Withdraw Request' : 'Reject Withdraw Request',
        description: approve
          ? `Approve SAR ${request.amount} to ${request.beneficiaryName}? The hold on the treasury bank is settled.`
          : `Reject SAR ${request.amount}? The hold goes back to the treasury bank.`,
        acceptButtonText: approve ? 'Approve' : 'Reject',
        rejectButtonText: 'Cancel',
        confirmButtonColor: approve ? 'success' : 'error',
        onConfirm: async () => {
          try {
            if (approve) {
              await approveWithdrawRequest({ companyId, id: request.id }).unwrap();
            } else {
              await rejectWithdrawRequest({ companyId, id: request.id }).unwrap();
            }

            enqueueSnackbar(approve ? 'Withdraw request approved' : 'Withdraw request rejected', {
              variant: 'success',
            });
          } catch (decideError) {
            enqueueSnackbar(getTreasuryErrorMessage(decideError, 'Failed to update the withdraw request'), {
              variant: 'error',
            });
          }
        },
      });

    return [
      { field: 'id', headerName: 'ID', width: 90, sortable: false },
      { field: 'amount', headerName: 'Amount (SAR)', width: 140, sortable: false },
      {
        field: 'status',
        headerName: 'Status',
        width: 130,
        sortable: false,
        renderCell: ({ row }) => (
          <Chip
            size="small"
            label={row.status}
            color={TREASURY_WITHDRAW_STATUS_COLORS[String(row.status).toLowerCase()] ?? 'default'}
          />
        ),
      },
      { field: 'beneficiaryName', headerName: 'Beneficiary', width: 180, sortable: false },
      { field: 'beneficiaryIban', headerName: 'IBAN', width: 220, sortable: false },
      { field: 'beneficiarySwiftCode', headerName: 'SWIFT', width: 120, sortable: false },
      { field: 'reason', headerName: 'Reason', width: 200, sortable: false },
      {
        field: 'createdAt',
        headerName: 'Created',
        width: 170,
        sortable: false,
        valueFormatter: ({ value }) => (value ? moment(value).format('MM/DD/YYYY HH:mm') : ''),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 180,
        sortable: false,
        renderCell: ({ row }) =>
          String(row.status).toLowerCase() === TREASURY_WITHDRAW_PENDING && (
            <>
              <Button size="small" color="success" disabled={!canUpdate} onClick={() => decide(row, true)}>
                Approve
              </Button>
              <Button size="small" color="error" disabled={!canUpdate} onClick={() => decide(row, false)}>
                Reject
              </Button>
            </>
          ),
      },
    ];
  }, [companyId, canUpdate, approveWithdrawRequest, rejectWithdrawRequest, enqueueSnackbar]);

  return (
    <>
      <TableCard
        title="Withdraw Requests"
        description="From the company treasury bank. Only a pending request can be approved or rejected."
        action={
          <Stack direction="row" spacing={1}>
            <Button size="small" startIcon={<RefreshIcon />} onClick={refetch}>
              Refresh
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              disabled={!canCreate}
              onClick={() => setIsDialogOpen(true)}
            >
              New Request
            </Button>
          </Stack>
        }
      >
        <RtkQueryTable
          key={tableKey}
          models={tableModels}
          onModelsChange={setTableModels}
          rows={data?.items ?? []}
          rowCount={data?.total ?? 0}
          columns={columns}
          loading={isFetching}
          pageSizeOptions={[TREASURY_LIST_PAGE_SIZE]}
          disableColumnFilter
          wrapperHeight={TREASURY_GRID_HEIGHT}
        />
      </TableCard>

      <TreasuryWithdrawDialog
        open={isDialogOpen}
        isSaving={isSaving}
        onClose={() => setIsDialogOpen(false)}
        onSave={create}
      />
      <AsyncDialog ref={confirmRef} dialog={ConfirmationDialog} />
    </>
  );
};
